// שיחות, תורים, קונטיינרים, SSE (10 §1–2).
import { z } from 'zod';
import { Id, Json, Ts, Usage, rowBase } from './common.ts';
import { Block, NoticeKind } from './block.ts';

export const ConversationStatus = z.enum(['active', 'idle', 'archived']);
export type ConversationStatus = z.infer<typeof ConversationStatus>;

/** sys.conversation */
export const Conversation = z.object({
  ...rowBase,
  userId: Id,
  level: z.union([z.literal(1), z.literal(2)]),
  titleHe: z.string().nullable(),
  status: ConversationStatus,
  containerId: z.string().nullable(),
  workspacePath: z.string(),
  sdkSessionId: z.string().nullable(),
  lastActiveAt: Ts,
  usage: Usage.extend({ turns: z.number().int().nonnegative() }).nullable(),
  /** conversation-context-delta: מה שה-session של ה-SDK כבר קיבל מההקשר (חתימות). NULL = התור הבא מלא. */
  contextSent: z.object({ session: z.string(), sigs: z.record(z.string(), z.tuple([z.string(), z.string(), z.string()])) }).nullable(),
});
export type Conversation =z.infer<typeof Conversation>;

export const TurnStatus = z.enum(['queued', 'running', 'done', 'error', 'canceled', 'timeout']);
export type TurnStatus = z.infer<typeof TurnStatus>;

/** sys.turn */
export const Turn = z.object({
  ...rowBase,
  conversationId: Id,
  seq: z.number().int().nonnegative(),
  userText: z.string(),
  status: TurnStatus,
  startedAt: Ts.nullable(),
  finishedAt: Ts.nullable(),
  usage: Usage.nullable(),
  errorHe: z.string().nullable(),
});
export type Turn = z.infer<typeof Turn>;

/** sys.turn_event — PK (turn_id, seq); payload אחרי scrub. */
export const TurnEvent = z.object({
  turnId: Id,
  seq: z.number().int().nonnegative(),
  instanceId: Id,
  createdAt: Ts,
  updatedAt: Ts.nullable(),
  type: z.string(),
  payload: Json,
  ts: Ts,
});
export type TurnEvent = z.infer<typeof TurnEvent>;

/** sys.conversation_ref — מה השיחה יצרה/שינתה: יצירה (slug) או פריסה (key) והפעולה; נרשם בכלי artifact.* / spread.* בשיחה (10 §1). */
export const ConversationRefKind = z.enum(['artifact', 'spread']);
export type ConversationRefKind = z.infer<typeof ConversationRefKind>;
export const ConversationRefAction = z.enum(['create', 'version', 'publish', 'rollback', 'freeze', 'unfreeze']);
export type ConversationRefAction = z.infer<typeof ConversationRefAction>;
export const ConversationRef = z.object({
  ...rowBase,
  conversationId: Id,
  turnId: Id.nullable(),
  kind: ConversationRefKind,
  refKey: z.string().min(1),
  action: ConversationRefAction,
});
export type ConversationRef = z.infer<typeof ConversationRef>;

/** קובץ שצורף להודעה בשיחה (10 §1 קבצים מצורפים) — מה שהמסך רואה ב-history / ב-user_message (בלי נתיבים). */
export const ConversationAttachment = z.object({ id: Id, name: z.string().min(1), mime: z.string().min(1), bytes: z.number().int().nonnegative() });
export type ConversationAttachment = z.infer<typeof ConversationAttachment>;

/** sys.conversation_file — הקובץ עצמו תחת AIBIFY_DATA_ROOT/files/uploads/<conversationId>/ (ה-api כותב, הסוכן רואה ב-/workspace/uploads קריאה בלבד). */
export const ConversationFile = z.object({
  ...rowBase,
  conversationId: Id,
  /** התור שההודעה שלו נושאת את הקובץ (null — הועלה וטרם נשלח). */
  turnId: Id.nullable(),
  /** הודעה באמצע תור (user_message.sayId) — אחרת null = הודעת הפתיחה של התור. */
  sayId: Id.nullable(),
  name: z.string().min(1),
  mime: z.string().min(1),
  bytes: z.number().int().nonnegative(),
  /** נתיב יחסי ל-AIBIFY_DATA_ROOT. */
  path: z.string().min(1),
  /** המרה לטקסט (xlsx/xls/docx/pdf → .txt ליד הקובץ) — יחסי; null כשאין. */
  textPath: z.string().nullable(),
  sha256: z.string().length(64),
  createdBy: Id,
  /** user = צורף להודעה; assistant = הסוכן שלח למשתמש (chat.sendFile / קובץ של ריצת יצירה מהשיחה). */
  author: z.enum(['user', 'assistant']),
  /** קובץ של ריצת יצירה (sys.file) שהועתק לשיחה — למניעת כפילות באותו תור. */
  sourceFileId: Id.nullable(),
  /** נמחק מהדיסק (30 יום אחרי ארכוב השיחה). */
  deletedAt: Ts.nullable(),
});
export type ConversationFile = z.infer<typeof ConversationFile>;

export const ContainerKind = z.enum(['conversation', 'artifact_run', 'adapter', 'session']);
export type ContainerKind = z.infer<typeof ContainerKind>;

/** sys.container — id = docker id. */
export const Container = z.object({
  id: z.string().min(1),
  instanceId: Id,
  createdAt: Ts,
  updatedAt: Ts.nullable(),
  kind: ContainerKind,
  ownerId: Id,
  image: z.string(),
  startedAt: Ts,
  stoppedAt: Ts.nullable(),
  stopReason: z.string().nullable(),
});
export type Container = z.infer<typeof Container>;

/** צעד מוצע להמשך (15): labelHe על הכפתור, prompt = ההודעה המלאה שנשלחת בשם המשתמש. */
export const Suggestion = z.object({ labelHe: z.string().min(1).max(40), prompt: z.string().min(1).max(500) });
export type Suggestion = z.infer<typeof Suggestion>;
/** בדיוק שלוש; פחות/יותר → האירוע נדחה (אין הצעות → אין כפתורים, לעולם לא מומצא במסך). */
export const SUGGESTIONS_COUNT = 3;

/** איך בלוק מ-display.preview מוצג: בתשובה (ברירת מחדל) או גם בהגדלה. */
export const BlockFocus = z.enum(['inline', 'expand']);
export type BlockFocus = z.infer<typeof BlockFocus>;

/** אירועי SSE (`id: <seq>`, `event: <type>`). */
export const SseEvent = z.discriminatedUnion('type', [
  // userText/attachments — הודעת הפתיחה של התור (מסך שלא שלח אותה — תור המשך, לשונית אחרת — מציג אותה בלי reload);
  // clientId — מזהה השליחה מהלקוח (ניסיון חוזר אידמפוטנטי, איחוד עם הבועה האופטימית); followUpOf — תור שנפתח מהודעות באמצע תור שהסוכן לא קלט
  z.object({ type: z.literal('queued'), userText: z.string().optional(), attachments: z.array(ConversationAttachment).optional(), clientId: z.string().max(64).optional(), followUpOf: z.string().optional() }),
  z.object({ type: z.literal('started'), containerId: z.string() }),
  z.object({ type: z.literal('text'), delta: z.string() }),
  // 10 §1 הודעה באמצע תור: המשתמש שלח בזמן שהתור רץ (נשמרת במקום שנשלחה בתמליל); heard — הסוכן קיבל אותה לתוך אותו תור
  z.object({ type: z.literal('user_message'), sayId: z.uuid(), text: z.string(), midTurn: z.literal(true), attachments: z.array(ConversationAttachment).optional(), clientId: z.string().max(64).optional() }),
  z.object({ type: z.literal('heard'), sayId: z.uuid() }),
  z.object({ type: z.literal('tool_use'), name: z.string(), summaryHe: z.string() }),
  z.object({ type: z.literal('tool_result'), name: z.string(), ok: z.boolean(), summaryHe: z.string() }),
  // focus (web-ai-stage-layout): 'inline' (ברירת מחדל, גם כשחסר — אירועים ישנים) = בתשובה בשיחה; 'expand' = גם נפתח בהגדלה
  // (רק באירוע חי; ב-replay מתעלמים). אייבי שולחת expand רק כשהמשתמש ביקש לראות בגדול.
  z.object({ type: z.literal('block'), block: Block, asOf: z.string().optional(), focus: BlockFocus.optional() }),
  // קובץ שהסוכן שלח למשתמש (chat.sendFile) או שריצת יצירה מהשיחה הפיקה (artifact.testRun) — נשמר בשיחה, להורדה (GET …/files/:fileId).
  // נוצר רק בשרת (ConversationService.sendAssistantFile); אירוע file שמגיע מהסוכן נזרק.
  z.object({ type: z.literal('file'), file: ConversationAttachment, captionHe: z.string().max(500).optional() }),
  z.object({ type: z.literal('consent_pending'), consentId: Id }),
  z.object({ type: z.literal('consent_decided'), consentId: Id, status: z.string() }),
  // 20: אין Gmail מחובר (no_mailbox) לכלי מייל בתור הזה — המסך פותח את "חיבור Gmail" בדיוק עכשיו. נוצר רק ב-api (internal/routes).
  z.object({ type: z.literal('mail_connect'), caps: z.array(z.enum(['send', 'read'])).min(1).max(2) }),
  z.object({ type: z.literal('notice'), kind: NoticeKind, textHe: z.string() }),
  z.object({ type: z.literal('error'), textHe: z.string() }),
  // 11 (2026-10-16): startedAt/finishedAt של sys.turn (ISO) — "עבד 1:47 דק'" ו"לפני 3 דקות" בקיפול של התור; מהשרת, לא משעון הדפדפן.
  // startedAt null — התור לא התחיל לרוץ (בוטל/נפל בתור). אופציונלי: done של הסוכן לא נושא אותם, ה-api מוסיף.
  z.object({ type: z.literal('done'), status: z.enum(['done', 'error', 'canceled', 'timeout']), usage: Usage, startedAt: z.string().nullable().optional(), finishedAt: z.string().optional() }),
  z.object({ type: z.literal('heartbeat') }),
  z.object({ type: z.literal('suggestions'), items: z.array(Suggestion).length(SUGGESTIONS_COUNT) }), // 15: "להמשך" — בסוף התור, מהמודל דרך turn.suggest
]);
export type SseEvent = z.infer<typeof SseEvent>;

// ───────────── conversation-memory (OpenSpec, 2026-10-17): זיכרון בין שיחות ─────────────
export const MemoryKind = z.enum(['user', 'work']);
export type MemoryKind = z.infer<typeof MemoryKind>;
export const MemoryScope = z.enum(['general', 'anchored']);
export type MemoryScope = z.infer<typeof MemoryScope>;
export const MemoryAnchorType = z.enum(['spread', 'artifact']);
export type MemoryAnchorType = z.infer<typeof MemoryAnchorType>;
export const MemorySource = z.enum(['aibi', 'explicit', 'edited']);
export const MemoryStatus = z.enum(['active', 'superseded']);
/** תקרת אורך של פריט זיכרון (משפט אחד). */
export const MEMORY_TEXT_MAX = 300;

/** sys.memory — פריט אחד של משתמש אחד; general נטען תמיד, anchored (פריסה לפי key / יצירה לפי slug) — רק בנגיעה. */
export const Memory = z.object({
  ...rowBase,
  userId: Id,
  kind: MemoryKind,
  scope: MemoryScope,
  anchorType: MemoryAnchorType.nullable(),
  anchorKey: z.string().regex(/^[A-Za-z0-9_.-]{1,80}$/).nullable(),
  textHe: z.string().min(1).max(MEMORY_TEXT_MAX),
  source: MemorySource,
  conversationId: Id.nullable(),
  turnId: Id.nullable(),
  status: MemoryStatus,
  supersedesId: Id.nullable(),
});
export type Memory = z.infer<typeof Memory>;

/** sys.memory_stat — מדדי כיול של הזיכרון ליום (מונים בלבד; make health). */
export const MemoryStat = z.object({
  ...rowBase,
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  saves: z.number().int().nonnegative(),
  explicitSaves: z.number().int().nonnegative(),
  replaces7d: z.number().int().nonnegative(),
  forgets7d: z.number().int().nonnegative(),
  rejected: z.number().int().nonnegative(),
  rejectedSecret: z.number().int().nonnegative(),
  contextTurns: z.number().int().nonnegative(),
  contextChars: z.number().int().nonnegative(),
  pinned: z.number().int().nonnegative(),
});
export type MemoryStat = z.infer<typeof MemoryStat>;

// ───────────── conversation-org-memory (OpenSpec, 2026-10-26): זיכרון ארגוני ─────────────
export const OrgMemoryAnchorType = z.enum(['spread', 'artifact', 'world']);
export type OrgMemoryAnchorType = z.infer<typeof OrgMemoryAnchorType>;
export const OrgMemoryStatus = z.enum(['active', 'superseded', 'deleted']);
export type OrgMemoryStatus = z.infer<typeof OrgMemoryStatus>;
/** universe-drawers-context: מגירות הסמנטיקה של היקום. 'links' מוקרן מקשר בין עולמות (לא נשמר ב-sys.org_memory). */
export const OrgMemoryDrawer = z.enum(['entities', 'rules', 'people', 'processes']);
export type OrgMemoryDrawer = z.infer<typeof OrgMemoryDrawer>;
export const SemanticDrawer = z.enum(['entities', 'rules', 'people', 'processes', 'links']);
export type SemanticDrawer = z.infer<typeof SemanticDrawer>;
export const SEMANTIC_DRAWER_HE: Record<SemanticDrawer, string> = { entities: 'ישויות', rules: 'כללים', people: 'אנשים ותפקידים', processes: 'תהליכים', links: 'קשרים בין עולמות' };
/** עוגן נראות: עולם, או ישות בעץ של אותו עולם. */
export const SemanticAnchor = z.object({ worldKey: z.string().regex(/^[a-z][a-z0-9_]{1,30}$/), entity: z.string().regex(/^[A-Za-z0-9_.-]{1,80}$/).nullable().optional() });
export type SemanticAnchor = z.infer<typeof SemanticAnchor>;

/** sys.org_memory — פריט של המופע (לא של משתמש): מי אמר, מי אישר ובאיזה כרטיס; מחיקה = status deleted (נשאר ביומן). */
export const OrgMemory = z.object({
  ...rowBase,
  scope: MemoryScope,
  anchorType: OrgMemoryAnchorType.nullable(),
  anchorKey: z.string().regex(/^[A-Za-z0-9_.-]{1,80}$/).nullable(),
  textHe: z.string().min(1).max(MEMORY_TEXT_MAX),
  saidBy: Id.nullable(),
  approvedBy: Id.nullable(),
  consentId: Id.nullable(),
  conversationId: Id.nullable(),
  turnId: Id.nullable(),
  status: OrgMemoryStatus,
  supersedesId: Id.nullable(),
  deletedBy: Id.nullable(),
  deletedConsentId: Id.nullable(),
  drawer: OrgMemoryDrawer,
  anchors: z.array(SemanticAnchor).max(20),
});
export type OrgMemory = z.infer<typeof OrgMemory>;

/** sys.org_memory_stat — מדדי הזיכרון הארגוני ליום (מונים בלבד; make health). */
export const OrgMemoryStat = z.object({
  ...rowBase,
  day: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  rejected: z.number().int().nonnegative(),
  contextTurns: z.number().int().nonnegative(),
  contextChars: z.number().int().nonnegative(),
  pinned: z.number().int().nonnegative(),
  hidden: z.number().int().nonnegative(),
  anchorsAdded: z.number().int().nonnegative(),
});
export type OrgMemoryStat = z.infer<typeof OrgMemoryStat>;
